import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Seo from '../components/Seo';
import { apiBaseUrl } from '../config';
import { useTurnstile } from '../hooks/useTurnstile';
import { setAdminSession, isAdminAuthenticated } from '../services/adminSession';
import logo from '../assets/logo-main.png';
import './AdminLogin.css';

const FEATURES = [
  {
    title: 'Centralised content control',
    desc: 'Publish blogs, manage courses and curate the homepage from one console.'
  },
  {
    title: 'Granular access policies',
    desc: 'Gate content for guests, logged-in learners or premium subscribers.'
  },
  {
    title: 'Hardened by design',
    desc: 'Short-lived sessions, audit-ready logs and bot-protected sign in.'
  }
];

const AdminLogin = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('expired') === '1';

  const {
    getTurnstileToken,
    resetTurnstile,
    turnstileRef,
    turnstileStatus,
    isTurnstileConfigured
  } = useTurnstile();

  // If a valid admin session already exists, jump straight to the dashboard.
  useEffect(() => {
    if (isAdminAuthenticated()) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  const year = useMemo(() => new Date().getFullYear(), []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    let turnstileToken;
    try {
      turnstileToken = await getTurnstileToken();
    } catch (err) {
      console.error('[AdminLogin] Turnstile error:', err.message);
      setError('Verification failed. Please wait a moment and try again.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${apiBaseUrl}/admin/user/signin`, {
        ...formData,
        turnstileToken
      });

      if (response.data.success) {
        resetTurnstile();
        setAdminSession(response.data.token, { roles: response.data.roles });
        navigate('/admin/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Sign in failed. Please try again.');
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  const turnstileVerifying = isTurnstileConfigured && turnstileStatus === 'pending';
  const turnstileBlocking =
    isTurnstileConfigured && turnstileStatus !== 'ready' && turnstileStatus !== 'error';
  const canSubmit = !loading && !turnstileBlocking && formData.email && formData.password;

  return (
    <div className="admin-shell">
      <Seo
        title="Admin Sign In — InvFly"
        description="InvFly administration sign-in."
        noIndex
      />

      <aside className="admin-shell__brand">
        <div className="admin-shell__brand-bg" aria-hidden="true" />

        <header className="admin-shell__brand-header">
          <img src={logo} alt="InvFly" className="admin-shell__brand-logo" />
          <span className="admin-shell__brand-pill">Admin Portal</span>
        </header>

        <div className="admin-shell__brand-body">
          <h1 className="admin-shell__brand-title">
            Run the InvFly platform with quiet confidence.
          </h1>
          <p className="admin-shell__brand-sub">
            A single workspace to publish learning, manage members and steer the business —
            built for the team behind every great trader.
          </p>

          <ul className="admin-shell__features">
            {FEATURES.map((f) => (
              <li key={f.title} className="admin-shell__feature">
                <span className="admin-shell__feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" width="14" height="14" fill="none">
                    <path
                      d="M4 10.5l3.2 3.2L16 5.5"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <div>
                  <div className="admin-shell__feature-title">{f.title}</div>
                  <div className="admin-shell__feature-desc">{f.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <footer className="admin-shell__brand-footer">
          <span>© {year} InvFly Technologies</span>
          <span className="admin-shell__brand-dot" aria-hidden="true">•</span>
          <span>Restricted access · authorised personnel only</span>
        </footer>
      </aside>

      <main className="admin-shell__form-pane">
        <div className="admin-shell__form-wrap">
          <div className="admin-shell__form-mobile-brand">
            <img src={logo} alt="InvFly" />
          </div>

          <span className="admin-shell__eyebrow">Secure sign in</span>
          <h2 className="admin-shell__title">Welcome back</h2>
          <p className="admin-shell__subtitle">
            Sign in with your administrator credentials to continue.
          </p>

          {sessionExpired && !error && (
            <div className="admin-shell__notice admin-shell__notice--info" role="status">
              <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.6" fill="none" />
                <path d="M10 6v4.5l3 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
              </svg>
              <span>Your session expired after 6 hours. Please sign in again.</span>
            </div>
          )}

          {error && (
            <div className="admin-shell__notice admin-shell__notice--error" role="alert">
              <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.6" fill="none" />
                <path d="M10 6v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <circle cx="10" cy="14" r="1" fill="currentColor" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="admin-shell__form" noValidate>
            <div className="admin-shell__field">
              <label htmlFor="admin-email">Email Address</label>
              <div className="admin-shell__input">
                <svg className="admin-shell__input-icon" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                  <path d="M3 5h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <path d="M2 6l8 5.5L18 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                </svg>
                <input
                  id="admin-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="you@invfly.com"
                />
              </div>
            </div>

            <div className="admin-shell__field">
              <label htmlFor="admin-password">Password</label>
              <div className="admin-shell__input">
                <svg className="admin-shell__input-icon" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                  <rect x="4" y="9" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <path d="M7 9V6.5a3 3 0 016 0V9" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
                <input
                  id="admin-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  className="admin-shell__pw-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                      <path d="M3 3l14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M10 4.5c-4 0-7 3-8.5 5.5 0.7 1.2 1.8 2.6 3.3 3.7M16.5 13.7C18 12.6 18.8 11.2 19.5 10c-1.5-2.5-4.5-5.5-9.5-5.5-1 0-1.9 0.1-2.7 0.3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                      <path d="M1.5 10C3 7.5 6 4.5 10 4.5s7 3 8.5 5.5c-1.5 2.5-4.5 5.5-8.5 5.5S3 12.5 1.5 10z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Cloudflare Turnstile — invisible by default, challenges only on suspicious traffic */}
            <div
              ref={turnstileRef}
              className="turnstile-widget-wrap turnstile-managed-mount admin-shell__turnstile"
              aria-hidden="true"
            />

            <button
              type="submit"
              className="admin-shell__submit"
              disabled={!canSubmit}
            >
              {loading ? (
                <>
                  <span className="admin-shell__spinner" aria-hidden="true" />
                  Signing in…
                </>
              ) : turnstileVerifying ? (
                'Verifying…'
              ) : (
                <>
                  Sign in
                  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                    <path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="admin-shell__form-meta">
            <span className="admin-shell__lock" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="12" height="12">
                <rect x="4" y="9" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                <path d="M7 9V6.5a3 3 0 016 0V9" stroke="currentColor" strokeWidth="1.5" fill="none" />
              </svg>
            </span>
            <span>
              Sessions expire after 6 hours of activity. Trouble signing in? Contact your
              workspace administrator.
            </span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLogin;
